import { Copy, Eye, Package, Printer, AlertTriangle, Tag, Check, SquareCheck, Square, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import type { Order } from "./OrderDetailModal";
import { formatUKDateTime } from "./OrderDetailModal";

interface OrdersTableProps {
  orders: Order[];
  onViewOrder: (order: Order) => void;
  selectedOrderIds: Set<string>;
  onToggleSelect: (orderId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onToggleFulfilled: (order: Order) => void;
  onPrintPackingSlip: (order: Order) => void;
}

export function OrdersTable({
  orders,
  onViewOrder,
  selectedOrderIds,
  onToggleSelect,
  onSelectAll,
  onDeselectAll,
  onToggleFulfilled,
  onPrintPackingSlip,
}: OrdersTableProps) {
  const getOrderAge = (dateStr: string) => {
    const created = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const formatAddress = (order: Order) => {
    const addr = order.delivery_address;
    return [
      order.customer_name,
      addr.line1,
      addr.line2,
      addr.city,
      addr.county,
      addr.postcode,
      addr.country,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const allSelected = orders.length > 0 && orders.every((o) => selectedOrderIds.has(o.id));
  const someSelected = orders.some((o) => selectedOrderIds.has(o.id));

  if (orders.length === 0) {
    return (
      <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 p-12 text-center">
        <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">No orders found</p>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/70 backdrop-blur-md border-slate-700/60 overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-700/60 hover:bg-slate-800/50">
              <TableHead className="w-10">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={allSelected ? onDeselectAll : onSelectAll}
                >
                  {allSelected ? (
                    <SquareCheck className="h-4 w-4 text-emerald-400" />
                  ) : someSelected ? (
                    <div className="h-4 w-4 border-2 border-slate-400 rounded-sm bg-slate-600" />
                  ) : (
                    <Square className="h-4 w-4 text-slate-400" />
                  )}
                </Button>
              </TableHead>
              <TableHead className="text-slate-300 text-xs">Order</TableHead>
              <TableHead className="text-slate-300 text-xs hidden sm:table-cell">Date</TableHead>
              <TableHead className="text-slate-300 text-xs">Customer</TableHead>
              <TableHead className="text-slate-300 text-xs hidden md:table-cell">Total</TableHead>
              <TableHead className="text-slate-300 text-xs">Status</TableHead>
              <TableHead className="text-slate-300 text-xs hidden lg:table-cell">Tracking</TableHead>
              <TableHead className="text-slate-300 text-xs w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              const isSelected = selectedOrderIds.has(order.id);
              const orderAge = getOrderAge(order.created_at);
              const isOverdue = !order.fulfilled && orderAge >= 3;
              const needsTracking = order.fulfilled && !order.tracking_number;

              return (
                <TableRow
                  key={order.id}
                  className={`border-slate-700/60 hover:bg-slate-800/50 ${
                    isSelected ? "bg-slate-800/30" : ""
                  } ${isOverdue ? "bg-red-900/10" : ""}`}
                >
                  <TableCell className="py-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(order.id)}
                      className="border-slate-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="font-mono text-xs text-slate-200">
                      {order.order_number}
                    </div>
                    <div className="text-[10px] text-slate-500 sm:hidden mt-0.5">
                      {formatUKDateTime(order.created_at)}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-200 whitespace-nowrap hidden sm:table-cell py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs">{formatUKDateTime(order.created_at)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-200 py-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-xs">{order.customer_name}</span>
                      <span className="text-[10px] text-slate-400 truncate max-w-[100px] sm:max-w-[130px]">
                        {order.customer_email}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-200 font-semibold hidden md:table-cell py-2 text-sm">
                    £{order.total.toFixed(2)}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-col gap-1">
                      <Badge
                        className={`text-[10px] h-5 w-fit ${
                          order.fulfilled
                            ? "bg-emerald-900/30 text-emerald-400 border-emerald-900/50"
                            : "bg-amber-900/30 text-amber-400 border-amber-900/50"
                        }`}
                      >
                        {order.fulfilled ? "Fulfilled" : "Unfulfilled"}
                      </Badge>
                      {isOverdue && (
                        <Badge className="bg-red-900/40 text-red-400 border-red-900/50 text-[10px] h-5 w-fit">
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                          Overdue
                        </Badge>
                      )}
                      {needsTracking && (
                        <Badge className="bg-blue-900/30 text-blue-400 border-blue-900/50 text-[10px] h-5 w-fit">
                          <Tag className="h-2.5 w-2.5 mr-0.5" />
                          Tracking
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-300 text-xs hidden lg:table-cell py-2">
                    {order.tracking_number ? (
                      <div className="flex flex-col">
                        <span className="font-mono text-[10px]">{order.tracking_number}</span>
                        {order.courier && (
                          <span className="text-[10px] text-slate-500 capitalize">
                            {order.courier.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewOrder(order)}
                        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20 h-7 px-2"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline ml-1 text-xs">View</span>
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-400 hover:text-slate-100 h-7 px-1.5"
                          >
                            •••
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700 w-44">
                          <DropdownMenuItem
                            onClick={() => copyToClipboard(order.customer_email, "Email")}
                            className="text-slate-200 hover:bg-slate-800 cursor-pointer text-xs"
                          >
                            <Mail className="h-3.5 w-3.5 mr-2" />
                            Copy Email
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => copyToClipboard(formatAddress(order), "Address")}
                            className="text-slate-200 hover:bg-slate-800 cursor-pointer text-xs"
                          >
                            <MapPin className="h-3.5 w-3.5 mr-2" />
                            Copy Address
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                          <DropdownMenuItem
                            onClick={() => onPrintPackingSlip(order)}
                            className="text-slate-200 hover:bg-slate-800 cursor-pointer text-xs"
                          >
                            <Printer className="h-3.5 w-3.5 mr-2" />
                            Print Packing Slip
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-slate-700" />
                          <DropdownMenuItem
                            onClick={() => onToggleFulfilled(order)}
                            className={`cursor-pointer text-xs ${
                              order.fulfilled
                                ? "text-amber-400 hover:bg-slate-800"
                                : "text-emerald-400 hover:bg-slate-800"
                            }`}
                          >
                            <Check className="h-3.5 w-3.5 mr-2" />
                            {order.fulfilled ? "Mark Unfulfilled" : "Mark Fulfilled"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
